/**
 * Where each App sits on the home screen — a launcher preference, not a fact
 * about the account.
 *
 * `groupByApp()` owns a domain rule: one tile per Compute, most recent
 * activity first. That rule is not replaced here. This is a LAYER on top of
 * its result, and while a person has never rearranged anything it does
 * nothing at all — the order stays exactly what grouping decided.
 *
 * ## Why `compute_id`, and only `compute_id`
 *
 * The tile is the App, and the App is the Compute. Display names are renamed,
 * instance ids belong to one set of data (an App has several, and removing
 * one must not move the tile), and array indices stop meaning anything the
 * moment a new App is added. `compute_id` is the only identifier that names
 * the same tile tomorrow.
 *
 * ## Why the stored value is only a list of ids
 *
 * Storing App objects would put a stale copy of the listing in localStorage:
 * a renamed App would come back with its old name, a removed one would come
 * back from the dead. The server's list is always the truth about WHICH Apps
 * exist; this only answers, for the ones that do exist, what order to draw
 * them in. Anything in storage that no longer exists is ignored, and anything
 * that exists but is not in storage goes to the end.
 *
 * ## Failure is not an error here
 *
 * Storage can be unavailable (Safari private mode throws on access, not just
 * on write), hold malformed JSON, or hold a shape from a version that no
 * longer exists. None of that is worth showing anybody a broken launcher
 * over: every failure path falls back to the default order and carries on.
 *
 * ## Deliberately not talking to the API
 *
 * v1 treats arrangement as a per-device preference, so it never touches
 * ato-api's schema. Everything that reads or writes storage is confined to
 * this file so that moving it to an account-level preference later is a
 * change to these four functions, not to the component.
 */

/** Bumped only when the stored SHAPE changes; an old key is ignored, not migrated. */
export const HOME_LAYOUT_STORAGE_KEY = "ato.my-apps.layout.v1";

/**
 * Read the saved order.
 *
 * Returns `null` — not `[]` — when there is nothing usable. The distinction
 * matters: `null` means "never arranged, use the default order", while an
 * empty array would mean "arranged into nothing", which is not a state a
 * person can produce and must not be inferred from a storage failure.
 */
export function readLayout(
  storage: Storage | null = safeStorage(),
): string[] | null {
  if (!storage) return null;
  let raw: string | null = null;
  try {
    raw = storage.getItem(HOME_LAYOUT_STORAGE_KEY);
  } catch {
    // Reading can throw, not just writing.
    return null;
  }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    // A bare array is the v1 shape. Anything else is a shape this version
    // does not know — an older schema, or something that is not ours.
    if (!Array.isArray(parsed)) return null;
    const ids = parsed.filter(
      (value): value is string => typeof value === "string" && value.length > 0,
    );
    return ids.length > 0 ? dedupe(ids) : null;
  } catch {
    return null;
  }
}

/** Save an order. A failure here is silent: arranging still worked on screen. */
export function writeLayout(
  order: readonly string[],
  storage: Storage | null = safeStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(HOME_LAYOUT_STORAGE_KEY, JSON.stringify(dedupe(order)));
  } catch {
    // Quota, private mode, or a disabled store. The arrangement is still
    // correct for this session; it just will not survive a reload.
  }
}

/** Forget the arrangement and fall back to the default order. */
export function clearLayout(storage: Storage | null = safeStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(HOME_LAYOUT_STORAGE_KEY);
  } catch {
    // Nothing to do and nothing worth reporting.
  }
}

/**
 * Apply a saved order to the Apps that actually exist right now.
 *
 * Three things happen at once, and all three are reconciliation rather than
 * trust in what was stored:
 *
 *   - ids in the layout that no longer exist are dropped (the App was removed
 *     on this device or another one),
 *   - Apps not mentioned by the layout are appended in their DEFAULT order,
 *     so a newly added App appears at the end where a launcher puts it,
 *   - duplicates collapse to their first position.
 *
 * With no layout, the input order is returned untouched: the default order is
 * `groupByApp()`'s and stays that way until somebody actually rearranges.
 */
export function applyLayout<T extends { compute_id: string }>(
  groups: readonly T[],
  layout: readonly string[] | null,
): T[] {
  return applyLayoutBy(groups, layout, (group) => group.compute_id);
}

/**
 * The same reconciliation for a grid whose tiles are not all Apps.
 *
 * The home screen also holds presets, which have no `compute_id` because they
 * have no ComputeInstance — and they are arranged alongside Apps, so one
 * order has to cover both. The identity is supplied by the caller rather than
 * read off a field, which keeps a preset id from ever being mistaken for a
 * compute_id: nothing here can hand an id to the API, because nothing here
 * knows which kind it is holding.
 */
export function applyLayoutBy<T>(
  items: readonly T[],
  layout: readonly string[] | null,
  identify: (item: T) => string,
): T[] {
  if (!layout || layout.length === 0) return [...items];
  const byId = new Map<string, T>();
  for (const item of items) {
    // First wins, so a duplicate id upstream cannot make one tile appear
    // twice on the home screen.
    const id = identify(item);
    if (!byId.has(id)) byId.set(id, item);
  }
  const ordered: T[] = [];
  const placed = new Set<string>();
  for (const id of layout) {
    if (placed.has(id)) continue;
    const item = byId.get(id);
    if (!item) continue; // Removed since the layout was written.
    ordered.push(item);
    placed.add(id);
  }
  // Everything the layout never mentioned, in the order it arrived.
  for (const item of items) {
    const id = identify(item);
    if (placed.has(id)) continue;
    ordered.push(item);
    placed.add(id);
  }
  return ordered;
}

/**
 * Move one App to where another one sits, and return the resulting order.
 *
 * Takes the CURRENTLY DISPLAYED order rather than the stored one. That is what
 * makes the first rearrangement work correctly: until now there was no stored
 * order, and the order a person is looking at — and reasoning about when they
 * drag — is grouping's. Persisting that snapshot at the moment of the first
 * drag is what freezes it, and nothing before that does.
 */
export function moveInLayout(
  currentOrder: readonly string[],
  activeId: string,
  overId: string,
): string[] {
  if (activeId === overId) return [...currentOrder];
  const from = currentOrder.indexOf(activeId);
  const to = currentOrder.indexOf(overId);
  if (from === -1 || to === -1) return [...currentOrder];
  const next = [...currentOrder];
  next.splice(from, 1);
  next.splice(to, 0, activeId);
  return next;
}

function dedupe(ids: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * `localStorage` when it is genuinely usable.
 *
 * Merely existing is not enough: Safari in private mode has thrown on ACCESS
 * in some versions, and an embedded context can have storage blocked by
 * policy. Touching it inside try/catch here is what keeps every caller from
 * needing its own guard.
 */
function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}
